import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  quoteFinancial,
  type EndorsementDetail,
  type QuoteValidationKind,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatRate } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { useToast } from '@/shared/ui/useToast';

import { regrasDoErroDoEndosso } from './endossoFinanceiro';

/**
 * Pecas do endosso com efeito financeiro (P-END-2) no passo "Resumo da alteracao": o
 * objeto do cosseguro (END-097, END-098), as aprovacoes automaticas (END-101 a END-104,
 * END-203) e os arquivos da cotacao de endosso ("Baixar resumo" e "Gerar minuta",
 * END-105, END-106).
 *
 * Decisao 2a do plano 49: os motores, o resumo e a minuta sao os da operacao do tipo
 * endosso (P-COT-4), e as validacoes automaticas respondem com as regras pendentes.
 */

/** Objeto do cosseguro do endosso, que e o da apolice endossada (END-097). */
export function EndorsementCoinsuranceObject({
  endorsement,
}: {
  endorsement: EndorsementDetail;
}) {
  const objeto = useQuery({
    queryKey: ['endorsement', endorsement.id, 'coinsurance'],
    queryFn: ({ signal }) =>
      quoteFinancial.endorsementCoinsurance(endorsement.id, signal),
    retry: false,
  });
  return (
    <Card>
      <CardHeader
        title="Objeto do cosseguro"
        description="O cosseguro da apolice endossada. A distribuicao do cosseguro no endosso e regra pendente (RES-005)."
      />
      <CardBody className="flex flex-col gap-2">
        {objeto.isError ? (
          <Alert
            tone="danger"
            title="Nao foi possivel carregar o objeto do cosseguro desta apolice."
          >
            {objeto.error.message}
          </Alert>
        ) : null}
        {objeto.isSuccess ? (
          objeto.data.coinsurers.length === 0 ? (
            <p className="text-sm text-ink-600">Nenhuma cosseguradora informada</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm text-ink-900">
              {objeto.data.coinsurers.map((item) => (
                <li key={`${item.name}-${item.position}`}>
                  {item.name}
                  {item.is_leader ? ' (Lider)' : ''} - {formatRate(item.share_bps)}
                </li>
              ))}
            </ul>
          )
        ) : null}
      </CardBody>
    </Card>
  );
}

const motores: {
  chave: string;
  titulo: string;
  validacao: QuoteValidationKind | null;
  regras: string;
}[] = [
  { chave: 'cadastro', titulo: 'cadastro', validacao: null, regras: 'SUBP-10, K7' },
  { chave: 'credito', titulo: 'credito', validacao: null, regras: 'SUBP-10, K7, C4' },
  {
    chave: 'subscricao',
    titulo: 'subscricao',
    validacao: 'underwriting-validation',
    regras: 'SUBP-10, K7',
  },
  {
    chave: 'resseguro',
    titulo: 'resseguro',
    validacao: 'reinsurance-validation',
    regras: 'RES-003, RES-005, SUBP-10',
  },
];

/**
 * Aprovacoes automaticas do endosso (END-101 a END-104, END-203). Cada uma diz o
 * resultado, a recusa ou o erro, com a regra pendente.
 */
export function EndorsementAutomaticApprovals({
  endorsement,
}: {
  endorsement: EndorsementDetail;
}) {
  const operacao = endorsement.operation ?? null;
  if (!operacao) return null;
  return (
    <Card>
      <CardHeader
        title="Aprovacoes automaticas"
        description="Os motores da cotacao de endosso. As regras de aprovacao automatica do endosso ainda nao foram homologadas: a analise segue a esteira."
      />
      <CardBody className="flex flex-col gap-3">
        {motores.map((motor) => (
          <LinhaDoMotor
            key={motor.chave}
            operationId={operacao.id}
            titulo={motor.titulo}
            validacao={motor.validacao}
            regras={motor.regras}
          />
        ))}
      </CardBody>
    </Card>
  );
}

function LinhaDoMotor({
  operationId,
  titulo,
  validacao,
  regras,
}: {
  operationId: string;
  titulo: string;
  validacao: QuoteValidationKind | null;
  regras: string;
}) {
  const { can } = useAuth();
  const executar = useMutation({
    mutationFn: () => {
      if (!validacao) throw new Error('sem validacao');
      return quoteFinancial.validate(operationId, validacao);
    },
  });
  const pendentes = regrasDoErroDoEndosso(executar.error);
  return (
    <div className="flex flex-col gap-1 rounded-md border border-line px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink-900">
          Aprovacao automatica de {titulo}.
        </p>
        {validacao && can('operacoes.operacao.update') ? (
          <AcaoComIcone
            acao="executar"
            rotulo="Executar"
            texto
            loading={executar.isPending}
            onClick={() => executar.mutate()}
          />
        ) : null}
        {titulo === 'credito' ? (
          <Link
            to={`/operacoes/${operationId}?aba=motor`}
            className="text-sm text-brand-600 underline-offset-2 hover:underline"
          >
            Historico do motor
          </Link>
        ) : null}
      </div>
      {executar.isError ? (
        <p className="text-2xs text-danger-700">
          Erro ao executar aprovacao automatica de {titulo}. {executar.error.message}
          {pendentes.length > 0 ? ` Regras pendentes: ${pendentes.join(', ')}.` : ''}
        </p>
      ) : (
        <p className="text-2xs text-ink-500">Regra pendente ({regras}).</p>
      )}
    </div>
  );
}

function salvarArquivo(conteudo: Blob, nome: string) {
  const url = URL.createObjectURL(conteudo);
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nome;
  document.body.append(ancora);
  ancora.click();
  ancora.remove();
  URL.revokeObjectURL(url);
}

/** "Baixar resumo" e "Gerar minuta" da cotacao de endosso (END-105, END-106). */
export function EndorsementQuoteFiles({
  endorsement,
}: {
  endorsement: EndorsementDetail;
}) {
  const toast = useToast();
  const operacao = endorsement.operation ?? null;
  const baixar = useMutation({
    mutationFn: () => {
      if (!operacao) throw new Error('Nao foi possivel baixar o resumo da cotacao.');
      return quoteFinancial.summary(operacao.id);
    },
    onSuccess: (conteudo) => {
      const extensao = conteudo.type.includes('pdf') ? 'pdf' : 'docx';
      salvarArquivo(conteudo, `resumo-do-endosso-${endorsement.number}.${extensao}`);
    },
    onError: () => toast.error('Nao foi possivel baixar o resumo da cotacao.'),
  });
  if (!operacao) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="secondary"
        loading={baixar.isPending}
        onClick={() => baixar.mutate()}
      >
        Baixar resumo
      </Button>
      <Link
        to={`/operacoes/${operacao.id}?aba=resumo`}
        className="text-sm text-brand-600 underline-offset-2 hover:underline"
      >
        Gerar minuta
      </Link>
    </div>
  );
}
